// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Modal from '@mui/material/Modal'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import ViewField from '@react/components/elements/ViewField'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { DefiCollateralSchema } from '@shared/CollateralSetCTO'
import { RoleEnum } from '@shared/Enums'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import { EthAddr } from '@shared/SchemaTypes'
import { DefiExchangeRateCTO, DefiExchangeRateCTOSchema, ExchangeRateChangeableStatus } from '@shared/defiCTO'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DefiSetExchangeRateForm from './DefiSetExchangeRateForm'

export default function DefiExchangeRateCard(props: {
  poolAddr: EthAddr
  defiExchangeRateCTO?: DefiExchangeRateCTO
  inFullPage: boolean
  refreshCallback?: () => void
}): React.ReactElement {
  const { poolAddr, defiExchangeRateCTO, inFullPage } = props
  const {
    userConnection: { roleEnum },
  } = useOtChainState()

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const navigate = useNavigate()

  const theme = useTheme()
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)

  const setExchangeRateAction = (): void => {
    setIsModalOpen(true)
  }

  useEffect(() => {
    window.addEventListener(
      'modalClose',
      () => {
        setIsModalOpen(false)
        props.refreshCallback?.()
      },
      false
    )

    return () => {
      window.removeEventListener(
        'modalClose',
        () => {
          setIsModalOpen(false)
        },
        false
      )
    }
  })

  const liquidityTokenSymbol = poolCTO.liquidityTokenSymbol
  const poolTokenSymbol = poolCTO.symbol

  if (
    !inFullPage &&
    (!defiExchangeRateCTO || (defiExchangeRateCTO.isAssetsReconciled && defiExchangeRateCTO.isNativeDefiReconciled))
  ) {
    return <></>
  }

  if (!defiExchangeRateCTO) {
    return (
      <Paper
        sx={{
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            marginTop: 10,
          }}
        >
          <Typography variant="h4">Exchange Rate Checker</Typography>
          <CircularProgress />
        </Box>
      </Paper>
    )
  }

  return (
    <Paper
      sx={{
        padding: 2,
        width: '100%',
      }}
    >
      <Typography variant="h4">Exchange Rate Checker</Typography>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <Box sx={{ display: 'flex', flex: 1 }}>
          <ViewField
            fieldSchema={DefiExchangeRateCTOSchema.describe().fields.currentExchangeRate}
            liquidityTokenSymbol={liquidityTokenSymbol}
            poolTokenSymbol={poolTokenSymbol}
            dataObject={defiExchangeRateCTO}
          />
        </Box>
        <Box sx={{ display: 'flex', flex: 1 }}>
          <ViewField
            fieldSchema={DefiExchangeRateCTOSchema.describe().fields.calibratedExchangeRate}
            liquidityTokenSymbol={liquidityTokenSymbol}
            poolTokenSymbol={poolTokenSymbol}
            dataObject={defiExchangeRateCTO}
          />
        </Box>
        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.exchangeRateChangeableStatus}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>

          {defiExchangeRateCTO.exchangeRateChangeableStatus === ExchangeRateChangeableStatus.ExcessiveChangeOnHold && (
            <Box
              sx={{
                border: ' 3px dashed red',
              }}
            >
              <ViewField
                fieldSchema={DefiCollateralSchema.describe().fields.exchangeRateMaximumAutoChange}
                liquidityTokenSymbol={liquidityTokenSymbol}
                poolTokenSymbol={poolTokenSymbol}
                dataObject={defiExchangeRateCTO.collateralCTO}
              />
            </Box>
          )}
          {defiExchangeRateCTO.exchangeRateChangeableStatus === ExchangeRateChangeableStatus.NoMaterialChange && (
            <Box
              sx={{
                border: ' 3px dashed yellow',
              }}
            >
              <ViewField
                fieldSchema={DefiCollateralSchema.describe().fields.exchangeRateMinimumMaterialChange}
                liquidityTokenSymbol={liquidityTokenSymbol}
                poolTokenSymbol={poolTokenSymbol}
                dataObject={defiExchangeRateCTO.collateralCTO}
              />
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              border: defiExchangeRateCTO.isNativeDefiReconciled ? '' : ' 3px dashed red',
            }}
          >
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.isNativeDefiReconciled}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box
            sx={{
              border: defiExchangeRateCTO.isAssetsReconciled ? '' : ' 3px dashed red',
            }}
          >
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.isAssetsReconciled}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: 2,
            border:
              defiExchangeRateCTO.exchangeRateChangeableStatus !== ExchangeRateChangeableStatus.ClearToChange
                ? '3px dashed red'
                : '',
            backgroundColor:
              defiExchangeRateCTO.exchangeRateChangeableStatus !== ExchangeRateChangeableStatus.ClearToChange
                ? '#FFD580'
                : '',
          }}
        >
          {roleEnum === RoleEnum.POOL_ADMIN && (
            <Button
              title={'Set Exchange Rate'}
              variant={'contained'}
              onClick={setExchangeRateAction}
              disabled={
                defiExchangeRateCTO.exchangeRateChangeableStatus === ExchangeRateChangeableStatus.Undefined ||
                isModalOpen
              }
            >
              Set Exchange Rate
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', flex: 4, flexDirection: 'column', gap: 2 }}>
          <a
            href={`https://etherscan.io/address/${defiExchangeRateCTO.collateralCTO?.defiVaultAddr}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textAlign: 'left', justifyContent: 'flex-start', color: theme.palette.accent1.emphasis }}
          >
            Etherscan {`${defiExchangeRateCTO.collateralCTO?.defiVaultAddr?.slice(0, 8)}...`}
          </a>
        </Box>

        {!inFullPage && (
          <Button
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/exchangeRate/${poolAddr}`)
            }}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              color: theme.palette.text.secondary,
              '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent',
              },
            }}
          >
            More details
          </Button>
        )}
      </Box>
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
          }}
        >
          <DefiSetExchangeRateForm poolAddr={poolAddr} defiExchangeRateCTO={defiExchangeRateCTO} />
        </Paper>
      </Modal>
    </Paper>
  )
}
